import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faHome,
  faChartLine,
  faCircle,
  faSignOutAlt,
  faUser,
  faBars,
  faTimes,
  faCode,
  faHistory,
  faBriefcase,
  faEye,
  faEyeSlash,
  faNewspaper,
  faShieldAlt,
  faBell,
  faStickyNote,
  faUniversity,
  faCoins,
  faChartArea,
} from '@fortawesome/free-solid-svg-icons';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { QuoteProvider } from './contexts/QuoteContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ThemePicker from './components/ThemePicker';
import { AlertsProvider, useAlerts } from './contexts/AlertsContext';
import { EarningsBookProvider } from './contexts/EarningsBookContext';
import { SymbolNotesProvider, useSymbolNotes } from './contexts/SymbolNotesContext';
import { RiskProvider, useRisk } from './contexts/RiskContext';
import { OrderPresetsProvider } from './contexts/OrderPresetsContext';
import { LiveStreamProvider, useLiveStream } from './contexts/LiveStreamContext';
import { LiveStreamPip } from './components/LiveStream';
import Dashboard from './pages/Dashboard';
import SettingsPage from './pages/SettingsPage';
import AssetDetailsPage from './pages/AssetDetailsPage';
import LoginPage from './pages/LoginPage';
import McpOAuthAuthorizePage from './pages/McpOAuthAuthorizePage';
import AlgorithmicTradingPage from './pages/AlgorithmicTradingPage';
import AccountActivitiesPage from './pages/AccountActivitiesPage';
import AdvancedChartingPage from './pages/AdvancedChartingPage';
import PortfolioPage from './pages/PortfolioPage';
import NewsPage from './pages/NewsPage';
import SchwabPage from './pages/SchwabPage';
import CryptoTradingPage from './pages/CryptoTradingPage';
import IndicatorsPage from './pages/IndicatorsPage';
import AssetSearch from './components/AssetSearch';
import ProtectedRoute from './components/ProtectedRoute';
import AIChat from './components/AIChat';
import QuotePanel from './components/QuotePanel';
import MarketIndexTape from './components/MarketIndexTape';
import AlertsPanel from './components/AlertsPanel';
import NotesPanel from './components/NotesPanel';
import RiskPanel from './components/RiskPanel';
import { accountApi } from './api/accountApi';
import './App.css';

// Initialize trading environment in localStorage on app load (before any components mount)
if (!localStorage.getItem('tradingEnv')) {
  localStorage.setItem('tradingEnv', 'paper');
}

// Header component with navigation
const Header = () => {
  const location = useLocation();
  const { user, logout, isAuthenticated, maskAmounts, setMaskAmounts, alpacaOnly, setAlpacaOnly } = useAuth();
  const { noteCount } = useSymbolNotes();
  const { alerts } = useAlerts();
  const { settings, dayPl } = useRisk();
  const { pipEnabled, togglePipEnabled } = useLiveStream();
  const isDashboard = location.pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [environment, setEnvironment] = useState(null);
  const [envLoading, setEnvLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deskPanel, setDeskPanel] = useState(null); // 'risk' | 'notes' | 'alerts'
  const [tradingEnv, setTradingEnv] = useState(() => {
    // Initialize from localStorage, default to 'paper' if not set
    const env = localStorage.getItem('tradingEnv') || 'paper';
    // Ensure localStorage is set
    if (!localStorage.getItem('tradingEnv')) {
      localStorage.setItem('tradingEnv', 'paper');
    }
    return env;
  });

  const activeAlerts = alerts.filter((a) => a.active && !a.triggeredAt).length;
  const triggeredAlerts = alerts.filter((a) => a.triggeredAt).length;
  const riskLimitBreached =
    settings.enabled &&
    settings.dailyLossLimitUsd != null &&
    dayPl != null &&
    dayPl <= -Math.abs(settings.dailyLossLimitUsd);

  const openDeskPanel = (panel) => {
    setDeskPanel(panel);
    setMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Display the environment based on localStorage, not API response
    // The API response reflects what environment the API is using based on the header we send
    const currentEnv = localStorage.getItem('tradingEnv') || 'paper';
    setEnvironment(currentEnv === 'live' ? 'LIVE' : 'PAPER');
    setTradingEnv(currentEnv);
  }, [isAuthenticated]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className={`sticky top-0 z-50 border-b border-hairline bg-surface/95 backdrop-blur-md ${
      scrolled ? '' : ''
    }`}>
      <div className="mx-auto max-w-[1600px] px-4 lg:px-6">
        <div className="flex items-center justify-between h-12 gap-3">
          <Link
            to="/"
            className="flex items-center group space-x-2 hover:opacity-90 transition-opacity flex-shrink-0"
          >
            <div className="rounded-md p-1.5 border border-hairline bg-elevated">
              <svg
                className="w-4 h-4 md:w-5 md:h-5 text-ink"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 2C8 2 5 5 5 9C5 11 6 12.5 7.5 13.5C7 14 6.5 14.5 6 15C5.5 15.5 5 16 5 17C5 18 5.5 19 6.5 19C7 19 7.5 18.5 8 18C8.5 17.5 9 17 9.5 17H14.5C15 17 15.5 17.5 16 18C16.5 18.5 17 19 17.5 19C18.5 19 19 18 19 17C19 16 18.5 15.5 18 15C17.5 14.5 17 14 16.5 13.5C18 12.5 19 11 19 9C19 5 16 2 12 2ZM12 4C14.5 4 16.5 6 16.5 8.5C16.5 9.5 16 10.5 15.5 11C15.8 11.2 16 11.5 16.2 11.8C16.5 12.2 16.5 12.8 16.2 13.2C16 13.5 15.5 13.5 15.2 13.2C15 13 14.8 12.8 14.5 12.5H9.5C9.2 12.8 9 13 8.8 13.2C8.5 13.5 8 13.5 7.8 13.2C7.5 12.8 7.5 12.2 7.8 11.8C8 11.5 8.2 11.2 8.5 11C8 10.5 7.5 9.5 7.5 8.5C7.5 6 9.5 4 12 4Z"/>
                <circle cx="9.5" cy="9" r="1.2"/>
                <circle cx="14.5" cy="9" r="1.2"/>
              </svg>
            </div>
            <div className="hidden sm:block leading-tight">
              <div className="text-[13px] font-semibold tracking-wide text-ink">LionTrading</div>
              <div className="text-[9px] uppercase tracking-[0.16em] text-muted">Institutional desk</div>
            </div>
          </Link>

          {isAuthenticated && (
            <div className="hidden md:flex flex-1 max-w-xl mx-4">
              <AssetSearch />
            </div>
          )}

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {isAuthenticated && (
              <>
                <span className={`hidden sm:inline-flex items-center gap-1.5 h-8 px-2 rounded-md border text-[10px] font-semibold uppercase tracking-wider ${
                  environment === 'LIVE'
                    ? 'border-[rgba(61,220,151,0.35)] text-bull'
                    : 'border-[rgba(232,184,74,0.35)] text-[var(--lt-warning)]'
                }`}>
                  <FontAwesomeIcon icon={faCircle} className="text-[7px]" />
                  {envLoading ? '...' : environment === 'LIVE' ? 'Live' : 'Paper'}
                </span>
                <button
                  type="button"
                  onClick={() => setMaskAmounts(!maskAmounts)}
                  className="h-11 w-11 sm:h-8 sm:w-8 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink"
                  title={maskAmounts ? 'Show balances' : 'Hide balances'}
                >
                  <FontAwesomeIcon icon={maskAmounts ? faEyeSlash : faEye} className="text-xs" />
                </button>
              </>
            )}
            <ThemePicker compact />
            {isAuthenticated && (
              <div className="relative">
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="h-11 w-11 sm:h-8 sm:w-8 inline-flex items-center justify-center rounded-md border border-hairline text-ink hover:bg-elevated"
                  aria-label="Menu"
                >
                  <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} className="text-xs" />
                </button>

                {/* Dropdown Menu */}
                {menuOpen && (
                  <>
                    {/* Backdrop */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setMenuOpen(false)}
                    />
                    
                    {/* Menu */}
                    <div className="absolute right-0 mt-2 w-64 lt-card z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-hairline">
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon icon={faUser} className="text-muted text-sm" />
                          <span className="text-sm font-medium text-ink">{user?.name || user?.username || 'User'}</span>
                        </div>
                      </div>

                      {/* Navigation Links */}
                      <nav className="py-2">
                        <div className="px-4 pt-1 pb-1.5 text-[10px] uppercase tracking-[0.14em] text-muted">
                          Desk
                        </div>
                        <button
                          type="button"
                          onClick={() => openDeskPanel('risk')}
                          className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm min-h-[44px] text-muted hover:bg-elevated hover:text-ink transition-colors"
                        >
                          <span className="inline-flex items-center gap-3">
                            <FontAwesomeIcon
                              icon={faShieldAlt}
                              className={`text-xs ${riskLimitBreached ? 'text-bear' : 'text-action'}`}
                            />
                            <span>Risk</span>
                          </span>
                          {riskLimitBreached ? (
                            <span className="num text-[10px] px-1.5 py-0.5 rounded bg-bear text-white">LIMIT</span>
                          ) : !settings.enabled ? (
                            <span className="num text-[10px] px-1.5 py-0.5 rounded bg-elevated text-muted">OFF</span>
                          ) : null}
                        </button>
                        <button
                          type="button"
                          onClick={() => openDeskPanel('alerts')}
                          className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm min-h-[44px] text-muted hover:bg-elevated hover:text-ink transition-colors"
                        >
                          <span className="inline-flex items-center gap-3">
                            <FontAwesomeIcon icon={faBell} className="text-xs" />
                            <span>Alerts</span>
                          </span>
                          <span className="inline-flex items-center gap-1">
                            {activeAlerts > 0 && (
                              <span className="num text-[10px] px-1.5 py-0.5 rounded bg-action text-white">
                                {activeAlerts}
                              </span>
                            )}
                            {triggeredAlerts > 0 && (
                              <span className="num text-[10px] px-1.5 py-0.5 rounded bg-[var(--lt-warning)] text-[#0B0E14]">
                                {triggeredAlerts}
                              </span>
                            )}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => openDeskPanel('notes')}
                          className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm min-h-[44px] text-muted hover:bg-elevated hover:text-ink transition-colors"
                        >
                          <span className="inline-flex items-center gap-3">
                            <FontAwesomeIcon icon={faStickyNote} className="text-xs text-[var(--lt-warning)]" />
                            <span>Notes</span>
                          </span>
                          {noteCount > 0 && (
                            <span className="num text-[10px] px-1.5 py-0.5 rounded bg-[var(--lt-warning)] text-[#0B0E14]">
                              {noteCount}
                            </span>
                          )}
                        </button>

                        <div className="my-2 border-t border-hairline" />
                        <div className="px-4 pt-1 pb-1.5 text-[10px] uppercase tracking-[0.14em] text-muted">
                          Navigate
                        </div>

                        <Link
                          to="/"
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm min-h-[44px] transition-colors ${
                            isDashboard
                              ? 'bg-elevated text-ink'
                              : 'text-muted hover:bg-elevated hover:text-ink'
                          }`}
                        >
                          <FontAwesomeIcon icon={faHome} className="text-xs" />
                          <span>Dashboard</span>
                        </Link>
                        
                        <Link
                          to="/settings"
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm min-h-[44px] transition-colors ${
                            location.pathname === '/settings'
                              ? 'bg-elevated text-ink'
                              : 'text-muted hover:bg-elevated hover:text-ink'
                          }`}
                        >
                          <FontAwesomeIcon icon={faCog} className="text-xs" />
                          <span>Settings</span>
                        </Link>
                        
                        <Link
                          to="/algorithm"
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm min-h-[44px] transition-colors ${
                            location.pathname === '/algorithm'
                              ? 'bg-elevated text-ink'
                              : 'text-muted hover:bg-elevated hover:text-ink'
                          }`}
                        >
                          <FontAwesomeIcon icon={faCode} className="text-xs" />
                          <span>Algorithmic Trading</span>
                        </Link>
                        
                        <Link
                          to="/activities"
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm min-h-[44px] transition-colors ${
                            location.pathname === '/activities'
                              ? 'bg-elevated text-ink'
                              : 'text-muted hover:bg-elevated hover:text-ink'
                          }`}
                        >
                          <FontAwesomeIcon icon={faHistory} className="text-xs" />
                          <span>Account Activities</span>
                        </Link>
                        
                        <Link
                          to="/charting"
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm min-h-[44px] transition-colors ${
                            location.pathname === '/charting'
                              ? 'bg-elevated text-ink'
                              : 'text-muted hover:bg-elevated hover:text-ink'
                          }`}
                        >
                          <FontAwesomeIcon icon={faChartLine} className="text-xs" />
                          <span>Advanced Charting</span>
                        </Link>
                        
                        <Link
                          to="/portfolio"
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm min-h-[44px] transition-colors ${
                            location.pathname === '/portfolio'
                              ? 'bg-elevated text-ink'
                              : 'text-muted hover:bg-elevated hover:text-ink'
                          }`}
                        >
                          <FontAwesomeIcon icon={faBriefcase} className="text-xs" />
                          <span>Portfolio Tracker</span>
                        </Link>
                        
                        <Link
                          to="/news"
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm min-h-[44px] transition-colors ${
                            location.pathname === '/news'
                              ? 'bg-elevated text-ink'
                              : 'text-muted hover:bg-elevated hover:text-ink'
                          }`}
                        >
                          <FontAwesomeIcon icon={faNewspaper} className="text-xs" />
                          <span>Market News</span>
                        </Link>

                        <Link
                          to="/indicators"
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm min-h-[44px] transition-colors ${
                            location.pathname === '/indicators'
                              ? 'bg-elevated text-ink'
                              : 'text-muted hover:bg-elevated hover:text-ink'
                          }`}
                        >
                          <FontAwesomeIcon icon={faChartArea} className="text-xs" />
                          <span>Indicators</span>
                        </Link>

                        <Link
                          to="/crypto"
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm min-h-[44px] transition-colors ${
                            location.pathname === '/crypto'
                              ? 'bg-elevated text-ink'
                              : 'text-muted hover:bg-elevated hover:text-ink'
                          }`}
                        >
                          <FontAwesomeIcon icon={faCoins} className="text-xs" />
                          <span>Crypto Desk</span>
                        </Link>

                        <Link
                          to="/schwab"
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm min-h-[44px] transition-colors ${
                            location.pathname === '/schwab'
                              ? 'bg-elevated text-ink'
                              : 'text-muted hover:bg-elevated hover:text-ink'
                          }`}
                        >
                          <FontAwesomeIcon icon={faUniversity} className="text-xs" />
                          <span>Schwab Positions</span>
                        </Link>
                      </nav>

                      {/* Toggles */}
                      <div className="px-4 py-3 border-t border-hairline space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs text-ink">TV overlay</div>
                            <div className="text-[10px] text-muted">Keep live video when leaving dashboard</div>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={pipEnabled}
                            onClick={togglePipEnabled}
                            className={`relative h-6 w-11 rounded-full transition-colors ${
                              pipEnabled ? 'bg-action' : 'bg-elevated border border-hairline'
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                                pipEnabled ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs text-ink">Alpaca only</div>
                            <div className="text-[10px] text-muted">Hide combined totals</div>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={alpacaOnly}
                            onClick={() => setAlpacaOnly(!alpacaOnly)}
                            className={`relative h-6 w-11 rounded-full transition-colors ${
                              alpacaOnly ? 'bg-action' : 'bg-elevated border border-hairline'
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                                alpacaOnly ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted">Environment</span>
                          <div className="flex items-center border border-hairline rounded-md overflow-hidden text-[11px]">
                            <button
                              type="button"
                              onClick={() => {
                                setTradingEnv('paper');
                                localStorage.setItem('tradingEnv', 'paper');
                                window.location.reload();
                              }}
                              className={`px-3 py-2 min-h-[36px] transition-colors ${
                                tradingEnv === 'paper'
                                  ? 'bg-[var(--lt-warning)] text-[#0B0E14] font-semibold'
                                  : 'text-muted hover:bg-elevated'
                              }`}
                            >
                              Paper
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setTradingEnv('live');
                                localStorage.setItem('tradingEnv', 'live');
                                window.location.reload();
                              }}
                              className={`px-3 py-2 min-h-[36px] transition-colors border-l border-hairline ${
                                tradingEnv === 'live'
                                  ? 'bg-bull text-[#0B0E14] font-semibold'
                                  : 'text-muted hover:bg-elevated'
                              }`}
                            >
                              Live
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="px-4 py-3 border-t border-hairline">
                        <button
                          onClick={() => {
                            setMenuOpen(false);
                            handleLogout();
                          }}
                          className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 min-h-[44px] bg-bear text-white rounded-md hover:opacity-90 transition-opacity text-sm font-medium"
                        >
                          <FontAwesomeIcon icon={faSignOutAlt} className="text-xs" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {isAuthenticated && (
        <>
          <RiskPanel
            open={deskPanel === 'risk'}
            onClose={() => setDeskPanel(null)}
            hideTrigger
          />
          <AlertsPanel
            open={deskPanel === 'alerts'}
            onClose={() => setDeskPanel(null)}
            hideTrigger
          />
          <NotesPanel
            open={deskPanel === 'notes'}
            onClose={() => setDeskPanel(null)}
            hideTrigger
          />
        </>
      )}
    </header>
  );
};


// Main App component with routing
function AppContent() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const isLoginPage = location.pathname === '/login';
  const isMcpOAuthPage = location.pathname === '/oauth/authorize';
  const isAlgoPopout =
    location.pathname === '/algorithm' &&
    new URLSearchParams(location.search).get('popout') === '1';
  const hideChrome = isLoginPage || isMcpOAuthPage || isAlgoPopout;

  return (
    <div className="min-h-screen flex flex-col bg-canvas text-ink">
      {!hideChrome && <Header />}
      {!hideChrome && isAuthenticated && <MarketIndexTape />}
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/oauth/authorize" element={<McpOAuthAuthorizePage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/asset/:symbol"
          element={
            <ProtectedRoute>
              <AssetDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/algorithm"
          element={
            <ProtectedRoute>
              <AlgorithmicTradingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/activities"
          element={
            <ProtectedRoute>
              <AccountActivitiesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/charting"
          element={
            <ProtectedRoute>
              <AdvancedChartingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio"
          element={
            <ProtectedRoute>
              <PortfolioPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/news"
          element={
            <ProtectedRoute>
              <NewsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/indicators"
          element={
            <ProtectedRoute>
              <IndicatorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/crypto"
          element={
            <ProtectedRoute>
              <CryptoTradingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/schwab"
          element={
            <ProtectedRoute>
              <SchwabPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!hideChrome && isAuthenticated && <AIChat />}
      {!hideChrome && isAuthenticated && <QuotePanel />}
      {!hideChrome && isAuthenticated && <LiveStreamPip />}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <LiveStreamProvider>
          <QuoteProvider>
            <AlertsProvider>
              <EarningsBookProvider>
                <SymbolNotesProvider>
                  <RiskProvider>
                    <OrderPresetsProvider>
                      <Router>
                        <AppContent />
                      </Router>
                    </OrderPresetsProvider>
                  </RiskProvider>
                </SymbolNotesProvider>
              </EarningsBookProvider>
            </AlertsProvider>
          </QuoteProvider>
        </LiveStreamProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

